import { useSuspenseQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from 'src/constants';
import type { ResolvedSearchProfile } from 'app/domain';
import useApi from './useApi';
import type SearchProfileService from './searchProfileService';

/**
 * The search-profile query: its key, its fetcher, and its caching, in one place.
 *
 * Exported so the route middleware can prefetch it before React runs, under
 * the same key `useSearchProfile` later reads. Takes the service rather than
 * the http client, so the client stays inside this directory.
 */
export const searchProfileQuery = (
  searchProfileService: SearchProfileService
) => ({
  queryKey: QUERY_KEYS.searchProfile,
  queryFn: (): Promise<ResolvedSearchProfile> => searchProfileService.get(),
  staleTime: Infinity,
  gcTime: Infinity,
});

/**
 * The parts of the visitor's search profile the BFF resolves server-side:
 * the geo-derived nudity facts and the visitor's auth state.
 *
 * Visitor-scoped and fixed for the session, so it is fetched once and reused
 * for the life of the page. Both options are load-bearing and do different
 * jobs: `staleTime: Infinity` stops it refetching while something observes it,
 * and `gcTime: Infinity` stops it being evicted when nothing does. Without the
 * second, the default five-minute collection drops it whenever no mounted
 * component holds it between navigations, and the next search pays for it
 * again.
 *
 * The app's QueryClient belongs to @unisporkal/federation — only test utilities
 * construct one here — so these are set per query rather than as client
 * defaults.
 *
 * `excludeNudity` is a **default**, not the visitor's choice. A stored
 * preference outranks it and only "never stated" falls through; keeping the two
 * apart is why the endpoint answers a default rather than a resolved
 * preference. `useSearchPreferences` takes it as an argument and applies the
 * `??` itself, so it stays synchronous — a click handler resolving preferences
 * must not wait on this.
 */
const useSearchProfile = (): ResolvedSearchProfile => {
  const { searchProfileService } = useApi();

  return useSuspenseQuery<ResolvedSearchProfile>(
    searchProfileQuery(searchProfileService)
  ).data;
};

export default useSearchProfile;
